import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 0,
  },

  searchRow: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5, 1.5, 1, 1.5),
    gap: 6,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },

  searchInputWrapper: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#F0F3F8",
    borderRadius: 10,
    padding: "4px 10px",
    gap: 6,
  },

  newTicketBtn: {
    width: 34,
    height: 34,
    borderRadius: 9,
    flexShrink: 0,
    color: "#fff",
    backgroundColor: theme.palette.primary.main,
    "&:hover": { backgroundColor: theme.palette.primary.dark },
  },

  searchIcon: {
    color: theme.palette.text.secondary,
    fontSize: 20,
    flexShrink: 0,
  },

  searchInput: {
    flex: 1,
    fontSize: "0.875rem",
  },

  // Abas sublinhadas (Atendendo / Aguardando / Resolvidos)
  chipsRow: {
    display: "flex",
    alignItems: "stretch",
    padding: theme.spacing(0, 1.5),
    gap: 18,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    overflowX: "auto",
    msOverflowStyle: "none",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },

  filterChip: {
    height: 38,
    fontSize: "0.82rem",
    fontWeight: 500,
    cursor: "pointer",
    border: 0,
    borderRadius: 0,
    borderBottom: "2px solid transparent",
    backgroundColor: "transparent !important",
    color: theme.palette.text.secondary,
    transition: "color 0.15s ease, border-color 0.15s ease",
    flexShrink: 0,
    "&:hover": {
      color: theme.palette.text.primary,
    },
    "& .MuiChip-label": {
      paddingLeft: 0,
      paddingRight: 0,
      display: "flex",
      alignItems: "center",
    },
  },

  filterChipActive: {
    color: `${theme.palette.primary.main} !important`,
    borderBottomColor: `${theme.palette.primary.main} !important`,
  },

  chipCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#F0F3F8",
    color: theme.palette.text.primary,
    borderRadius: 10,
    padding: "0 6px",
    fontSize: "0.68rem",
    minWidth: 18,
    height: 17,
    marginLeft: 6,
    lineHeight: 1,
    fontWeight: 700,
  },

  // Aguardando em laranja, para chamar atenção
  chipCountPrimary: {},
  chipCountHot: {
    background: "#F08A3C",
    color: "#fff",
  },

  showAllBtn: {
    padding: 5,
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },

  showAllBtnActive: {
    color: theme.palette.primary.main,
  },

  listsWrapper: {
    flex: 1,
    overflow: "hidden",
    position: "relative",
  },

  listContainer: {
    height: "100%",
    overflow: "hidden",
  },

}));

const TicketsManagerTabs = ({ onCountsChange, tabRequest }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const searchInputRef = useRef();

  const [searchParam, setSearchParam] = useState("");
  const [activeFilter, setActiveFilter] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const { user } = useContext(AuthContext);
  const { profile } = user;

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    if (location.state?.forceTabOpen && activeFilter !== location.state.forceTabOpen) {
      setActiveFilter(location.state.forceTabOpen);
      history.replace({
        pathname: location.pathname,
        search: location.search,
        state: { ...location.state, forceTabOpen: undefined },
      });
    }
  }, [location, activeFilter, history]);

  // Repassa os contadores para a tela "sem chat aberto"
  useEffect(() => {
    if (typeof onCountsChange === "function") {
      onCountsChange({ open: openCount, pending: pendingCount });
    }
  }, [openCount, pendingCount, onCountsChange]);

  // Permite que a tela "sem chat aberto" troque a aba ao clicar num contador
  useEffect(() => {
    if (tabRequest?.tab) setActiveFilter(tabRequest.tab);
  }, [tabRequest]);

  useEffect(() => {
    if (user.profile.toUpperCase() === "ADMIN") {
      setShowAllTickets(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchTimeout = useRef();

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();
    clearTimeout(searchTimeout.current);
    if (searchedTerm === "") {
      setSearchParam("");
      return;
    }
    searchTimeout.current = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setSelectedTags(tags);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setSelectedUsers(users);
  };

  const ChipLabel = ({ text, count, isActive, hot }) => (
    <span style={{ display: "flex", alignItems: "center" }}>
      {text}
      {count > 0 && (
        <span className={clsx(classes.chipCount, { [classes.chipCountPrimary]: isActive, [classes.chipCountHot]: hot })}>
          {count}
        </span>
      )}
    </span>
  );

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => handleCloseOrOpenTicket(ticket)}
      />

      {/* Barra de busca sempre visível */}
      <div className={classes.searchRow}>
        <div className={classes.searchInputWrapper}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            inputRef={searchInputRef}
            placeholder={i18n.t("tickets.search.placeholder")}
            type="search"
            onChange={handleSearch}
          />
        </div>
        <Can
          role={user.profile}
          perform="tickets-manager:showall"
          yes={() => (
            <Tooltip title={showAllTickets ? "Mostrando todos os tickets" : "Ver apenas meus tickets"}>
              <IconButton
                size="small"
                className={clsx(classes.showAllBtn, { [classes.showAllBtnActive]: showAllTickets })}
                onClick={() => setShowAllTickets((prev) => !prev)}
              >
                <PeopleAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        />
        <TicketsQueueSelect
          selectedQueueIds={selectedQueueIds}
          userQueues={user?.queues}
          onChange={(values) => setSelectedQueueIds(values)}
        />
        <Tooltip title={i18n.t("ticketsManager.buttons.newTicket")}>
          <IconButton
            size="small"
            className={classes.newTicketBtn}
            onClick={() => setNewTicketModalOpen(true)}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {/* Chips de filtro */}
      {!searchParam && (
        <div className={classes.chipsRow}>
          <Chip
            size="small"
            variant="outlined"
            label={
              <ChipLabel
                text={i18n.t("ticketsList.assignedHeader")}
                count={openCount}
                isActive={activeFilter === "open"}
              />
            }
            className={clsx(classes.filterChip, { [classes.filterChipActive]: activeFilter === "open" })}
            onClick={() => setActiveFilter("open")}
          />
          <Chip
            size="small"
            variant="outlined"
            label={
              <ChipLabel
                text={i18n.t("ticketsList.pendingHeader")}
                count={pendingCount}
                isActive={activeFilter === "pending"}
                hot
              />
            }
            className={clsx(classes.filterChip, { [classes.filterChipActive]: activeFilter === "pending" })}
            onClick={() => setActiveFilter("pending")}
          />
          <Chip
            size="small"
            variant="outlined"
            label={i18n.t("tickets.tabs.closed.title")}
            className={clsx(classes.filterChip, { [classes.filterChipActive]: activeFilter === "closed" })}
            onClick={() => setActiveFilter("closed")}
          />
        </div>
      )}

      {/* Filtros de tags/usuários na busca */}
      {searchParam && (
        <>
          <TagsFilter onFiltered={handleSelectedTags} />
          {profile === "admin" && (
            <UsersFilter onFiltered={handleSelectedUsers} />
          )}
        </>
      )}

      {/* Listas de tickets */}
      <div className={classes.listsWrapper}>
        <div
          className={classes.listContainer}
          style={!searchParam && activeFilter === "open" ? {} : { display: "none" }}
        >
          <TicketsList
            status="open"
            showAll={showAllTickets}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setOpenCount(val)}
          />
        </div>
        <div
          className={classes.listContainer}
          style={!searchParam && activeFilter === "pending" ? {} : { display: "none" }}
        >
          <TicketsList
            status="pending"
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setPendingCount(val)}
            onStatusChange={setActiveFilter}
          />
        </div>
        <div
          className={classes.listContainer}
          style={!searchParam && activeFilter === "closed" ? {} : { display: "none" }}
        >
          <TicketsList
            status="closed"
            showAll={true}
            selectedQueueIds={selectedQueueIds}
          />
        </div>
        {searchParam && (
          <div className={classes.listContainer}>
            <TicketsList
              searchParam={searchParam}
              showAll={true}
              tags={selectedTags}
              users={selectedUsers}
              selectedQueueIds={selectedQueueIds}
            />
          </div>
        )}
      </div>

    </Paper>
  );
};

export default TicketsManagerTabs;
